import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircle } from 'react-bootstrap-icons';
import { AuthContext } from '../contexts/AuthContext';
import { useMaintenanceContext } from '../contexts/MaintenanceContext';
import LandingNav from '../components/shared/LandingNav';
import useIsMobile from '../hooks/useIsMobile';
import LandingBackdrop from '../components/landing/LandingBackdrop';
import './Landing.css';
import './HomeDesk.css';
import './Pricing.css';
import SiteFooter from '../components/shared/SiteFooter';
import { TRADESTATION_AOP_URL } from '../constants/tradestationReferral';
import {
  BILLING_PLANS,
  PLAN_DISPLAY_NAMES,
  PLAN_SHORT_NAMES,
  ESSENTIALS_ACCENT,
  PRO_GOLD,
  formatPlanPriceDisplay,
  getPlanPriceForInterval,
  getPlanPriceSuffix,
  consumeBillingCheckoutReturn,
} from '../components/billing/billingPlanData';
import { useBillingPlanActions } from '../components/billing/useBillingPlanActions';
import { setSignupPlan } from '../utils/signupPlan';
import BillingIntervalToggle from '../components/billing/BillingIntervalToggle';
import AnnualPlanPriceNote from '../components/billing/AnnualPlanPriceNote';
import PricingBrokerSupportTable from '../components/billing/PricingBrokerSupportTable';
import { PlanFeatureList, PlanFeatureItem } from '../components/billing/PlanFeatureList';
import Seo from '../components/Seo';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

const RULES_TOOLTIP =
  'Track alerts you the moment you break a rule. Trade disables buy and sell until the lockout expires. You can still manage open positions.';

const TRACK_FEATURES = [
  {
    label: 'Rule alerts when you break a limit',
    included: true,
    tooltip: 'Track alerts you the moment you break a rule.',
  },
  { label: 'Behavior analytics', included: true },
  { label: 'Positions, orders, and balance', included: true },
  { label: 'Watchlists, journals, and alerts', included: true },
  { label: 'Unlimited PrecisionAI', included: true },
];

const TRADE_FEATURES = [
  {
    label: 'Rules that block order placement',
    included: true,
    tooltip: 'When a trading rule is set to Enforce, buy and sell are disabled until the lockout expires. You can still manage open positions.',
  },
  { label: 'Live order entry', included: true },
  { label: 'Position size from your stop and risk', included: true },
  { label: 'Multi-take-profit brackets', included: true },
  { label: 'Live charts and indicators', included: true },
];

const COMPARISON_GROUPS = [
  {
    id: 'both',
    label: 'Both plans',
    rows: [
      {
        feature: 'Trading rules',
        essentials: 'Alerts',
        pro: 'Enforced',
        tooltip: RULES_TOOLTIP,
      },
      { feature: 'Behavior analytics', essentials: true, pro: true },
      { feature: 'Positions, orders, and balance', essentials: true, pro: true },
      { feature: 'Watchlists, journals, and alerts', essentials: true, pro: true },
      { feature: 'Unlimited PrecisionAI', essentials: true, pro: true },
    ],
  },
  {
    id: 'trade-only',
    label: 'Trade only',
    rows: [
      { feature: 'Live order entry', essentials: false, pro: true },
      { feature: 'Position size from your stop and risk', essentials: false, pro: true },
      { feature: 'Multi-take-profit brackets', essentials: false, pro: true },
      { feature: 'Live charts and indicators', essentials: false, pro: true },
    ],
  },
];

const PRICING_COMPARE_TOOLTIP_POPPER = {
  strategy: 'fixed',
  modifiers: [
    { name: 'offset', options: { offset: [0, 6] } },
    { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
  ],
};

const CheckIcon = ({ color = ESSENTIALS_ACCENT }) => (
  <>
    <span style={{ color, fontWeight: 700, fontSize: 15 }} aria-hidden>✓</span>
    <span className="visually-hidden">Included</span>
  </>
);
const XIcon = () => (
  <>
    <span className="pricing-compare-dash" aria-hidden>—</span>
    <span className="visually-hidden">Not included</span>
  </>
);

const TooltipIcon = ({ tip, id, placement = 'top', compareTable = false, label = 'this feature' }) => (
  <OverlayTrigger
    placement={placement}
    container={compareTable && typeof document !== 'undefined' ? document.body : undefined}
    popperConfig={compareTable ? PRICING_COMPARE_TOOLTIP_POPPER : undefined}
    overlay={<Tooltip id={id} className={compareTable ? 'pricing-compare-tooltip' : undefined}>{tip}</Tooltip>}
  >
    <button type="button" className="pricing-tooltip-trigger" aria-label={`More about ${label}`}>
      <InfoCircle size={11} color="currentColor" aria-hidden />
    </button>
  </OverlayTrigger>
);

const Pricing = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [billingInterval, setBillingInterval] = useState('monthly');
  const { handlePlanAction, getCtaState } = useBillingPlanActions(billingInterval);
  const { flags } = useFeatureFlags();
  const { connectTradeLaunched, supportedBrokerCount } = flags;
  const brokerCountLabel = supportedBrokerCount ? `${supportedBrokerCount} brokers` : 'multiple brokers';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('canceled') === 'true') {
      consumeBillingCheckoutReturn();
    }
  }, []);

  useEffect(() => {
    const hash = location.hash;
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  try {
    useMaintenanceContext();
  } catch (_) {}

  const handleStartTrial = (tier) => {
    const plan = tier === 'track' || tier === 'essentials' ? 'track' : 'trade';
    setSignupPlan(plan);
    if (!user) {
      try { sessionStorage.setItem('registration_redirect', '/onboarding'); } catch (_) {}
      window.location.href = `/register?redirect=/onboarding&plan=${plan}`;
      return;
    }
    navigate('/onboarding');
  };

  const onPlanSelect = (tier) => {
    if (!user) {
      handleStartTrial(tier);
      return;
    }
    handlePlanAction(tier, { cancelReturn: 'pricing', postPlanChangeNav: true });
  };

  const renderCta = (tier, accentColor, { darkLabel = false, guestLabel = 'Start Tracking' } = {}) => {
    const { isCurrentPlan, canCheckoutEarly, showPlanAction, busy, actionLabel } = getCtaState(tier);

    if (isCurrentPlan && canCheckoutEarly) {
      return (
        <div
          className="pricing-plan-cta-shell pricing-plan-cta-shell--split"
          style={{ '--pricing-cta-accent': accentColor }}
        >
          <span className="pricing-plan-cta-shell__label">Current plan</span>
          <span className="pricing-plan-cta-shell__sep" aria-hidden>·</span>
          <button
            type="button"
            onClick={() => onPlanSelect(tier)}
            disabled={busy}
            className="pricing-plan-cta-shell__link"
          >
            {busy ? 'Redirecting…' : 'Checkout'}
          </button>
        </div>
      );
    }

    if (isCurrentPlan) {
      return (
        <div className="pricing-plan-cta-shell pricing-plan-cta-shell--current" style={{ '--pricing-cta-accent': accentColor }}>
          ✓ Current Plan
        </div>
      );
    }

    if (showPlanAction) {
      return (
        <button
          type="button"
          onClick={() => onPlanSelect(tier)}
          disabled={busy}
          className="pricing-plan-cta pricing-plan-cta--outline"
          style={{
            '--pricing-cta-accent': accentColor,
            color: accentColor,
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Redirecting…' : actionLabel}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={() => handleStartTrial(tier)}
        className={`pricing-plan-cta pricing-plan-cta--filled${darkLabel ? ' pricing-plan-cta--ink' : ''}`}
        style={{ '--pricing-cta-accent': accentColor }}
      >
        {guestLabel}
      </button>
    );
  };

  const proPrice = getPlanPriceForInterval(BILLING_PLANS.trade, billingInterval);
  const priceSuffix = getPlanPriceSuffix(billingInterval);
  const tradeBrokers = connectTradeLaunched
    ? 'TradeStation, Alpaca, and more'
    : 'TradeStation';
  const pricingSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        name: PLAN_DISPLAY_NAMES.track,
        brand: { '@type': 'Brand', name: 'PrecisionTrader' },
        category: 'Trading risk management software',
        description:
          'Track is free. Journal and review here. Track alerts you the moment you break a rule. You place orders through your broker.',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: '0',
          availability: 'https://schema.org/InStock',
          url: 'https://www.precisiontrader.tech/pricing',
        },
      },
      {
        '@type': 'Product',
        name: PLAN_DISPLAY_NAMES.trade,
        brand: { '@type': 'Brand', name: 'PrecisionTrader' },
        category: 'Trading execution and risk management software',
        description:
          'Trade stops the order before it goes through, with charts, execution, and auto position sizing.',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: String(proPrice),
          availability: 'https://schema.org/InStock',
          url: 'https://www.precisiontrader.tech/pricing',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Can I cancel anytime?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. No cancellation fees or penalties.',
            },
          },
          {
            '@type': 'Question',
            name: 'How does the 14-day trial work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No credit card. After 14 days, subscribe to keep access. Cancel before then and you are not charged.',
            },
          },
          {
            '@type': 'Question',
            name: 'Do I need TradeStation?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: connectTradeLaunched
                ? `PrecisionTrader supports ${brokerCountLabel} including TradeStation, so TradeStation is not required.`
                : 'Yes, currently PrecisionTrader requires a TradeStation account.',
            },
          },
        ],
      },
    ],
  };

  return (
    <div className="pricing-page landing-page home-desk">
      <Seo
        title="Pricing & Plans | PrecisionTrader"
        description="Track is free and alerts you the moment you break a rule. Trade stops the order — charts, execution, and enforcement on TradeStation, Alpaca, and more."
        canonicalPath="/pricing"
        schema={pricingSchema}
        schemaId="pricing-jsonld"
      />
      <LandingBackdrop />
      <div className="landing-content-shell">
      <LandingNav />

      <section className="pricing-hero-section">
        <div className="pricing-hero-inner">
          <h1 className="pricing-hero-title">
            Enforce the rules you already believe in.
          </h1>
          <p className="pricing-hero-subtitle">
            Track alerts you the moment you break a rule. Trade stops the order before it goes through.
          </p>

          <div className="pricing-interval-wrap">
            <BillingIntervalToggle
              value={billingInterval}
              onChange={setBillingInterval}
              accentColor={ESSENTIALS_ACCENT}
            />
          </div>

          <div className={`pricing-plans-desk${isMobile ? ' pricing-plans-desk--stack' : ''}`}>
            <article className="pricing-plan-card pricing-plan-card--essentials">
              <div className="pricing-plan-card__header">
                <p className="pricing-plan-card__eyebrow pricing-plan-card__eyebrow--track">Alerts & Analytics</p>
                <h2 className="pricing-plan-card__name">{PLAN_SHORT_NAMES.track}</h2>
                <p className="pricing-plan-card__tagline">
                  Journal and review here. You place orders through your broker.
                </p>
                <div className="pricing-plan-card__price-row">
                  <span className="pricing-plan-card__amount pricing-plan-card__amount--essentials">
                    Free
                  </span>
                </div>
                <p className="pricing-plan-card__fit">
                  {connectTradeLaunched ? 'Many supported brokers' : 'Account data from TradeStation'}
                </p>
              </div>
              <div className="pricing-plan-card__features">
                <PlanFeatureList>
                  {TRACK_FEATURES.map((f) => (
                    <PlanFeatureItem
                      key={f.label}
                      label={f.label}
                      included={f.included}
                      accentColor={ESSENTIALS_ACCENT}
                      tooltip={f.tooltip}
                      tooltipId={`ess-${f.label}`}
                    />
                  ))}
                </PlanFeatureList>
              </div>
              <div className="pricing-plan-card__action">
                {renderCta('track', ESSENTIALS_ACCENT, { guestLabel: 'Start Tracking' })}
              </div>
            </article>

            <article className="pricing-plan-card pricing-plan-card--pro">
              <div className="pricing-plan-card__header">
                <p className="pricing-plan-card__eyebrow pricing-plan-card__eyebrow--trade">Stops the order</p>
                <h2 className="pricing-plan-card__name">{PLAN_SHORT_NAMES.trade}</h2>
                <p className="pricing-plan-card__tagline">
                  Charts and order entry here, with rules that can block order placement.
                </p>
                <div className="pricing-plan-card__price-row">
                  <span className="pricing-plan-card__amount pricing-plan-card__amount--pro">
                    {formatPlanPriceDisplay(proPrice)}
                  </span>
                  <span className="pricing-plan-card__suffix">{priceSuffix}</span>
                </div>
                {billingInterval === 'annual' && (
                  <AnnualPlanPriceNote
                    plan={BILLING_PLANS.trade}
                    billingInterval={billingInterval}
                  />
                )}
                <p className="pricing-plan-card__fit">
                  {connectTradeLaunched ? tradeBrokers : 'Orders through TradeStation'}
                </p>
              </div>
              <div className="pricing-plan-card__features">
                <p className="pricing-plan-card__includes">Everything in Track, plus</p>
                <PlanFeatureList>
                  {TRADE_FEATURES.map((f) => (
                    <PlanFeatureItem
                      key={f.label}
                      label={f.label}
                      included
                      accentColor={PRO_GOLD}
                      tooltip={f.tooltip}
                      tooltipId={`pro-${f.label}`}
                    />
                  ))}
                </PlanFeatureList>
              </div>
              <div className="pricing-plan-card__action">
                {renderCta('trade', PRO_GOLD, { darkLabel: true, guestLabel: 'Enforce Your Rules' })}
              </div>
            </article>
            <p className="pricing-trial-note pricing-trial-note--trade">
              14-day free trial. No credit card. Cancel anytime.
            </p>
          </div>
        </div>
      </section>

      <section id="compare-plans" className="pricing-section">
        <div className="pricing-section-inner pricing-section-inner--narrow">
          <h2 className="pricing-section-title">Compare plans</h2>
          <p className="pricing-section-note">
            {connectTradeLaunched ? (
              <>
                Many brokers are supported on the Track plan. The Trade plan requires your broker to support live trading and market data.{' '}
                <a href="#supported-brokers" className="pricing-inline-link">See supported brokers</a>.
              </>
            ) : (
              'Both plans connect through TradeStation for now. More brokers are planned.'
            )}
          </p>
          <div className="pricing-compare-table">
            <div className="pricing-compare-header">
              <span className="pricing-compare-feature-label">Feature</span>
              <span className="pricing-compare-tier-label pricing-compare-tier-label--essentials">Track</span>
              <span className="pricing-compare-tier-label pricing-compare-tier-label--pro">Trade</span>
            </div>

            {COMPARISON_GROUPS.map((group) => (
              <React.Fragment key={group.id}>
                <div className="pricing-compare-group">
                  <span className="pricing-compare-group__label">{group.label}</span>
                </div>
                {group.rows.map((row) => (
                  <div key={row.feature} className="pricing-compare-row">
                    <span className="pricing-compare-feature">
                      {row.feature}
                      {row.tooltip && (
                        <TooltipIcon
                          tip={row.tooltip}
                          id={`cmp-${group.id}-${row.feature}`}
                          placement="top"
                          compareTable
                          label={row.feature}
                        />
                      )}
                    </span>
                    <span className="pricing-compare-tier">
                      {row.essentials === true
                        ? <CheckIcon color={ESSENTIALS_ACCENT} />
                        : row.essentials === false
                          ? <XIcon />
                          : <span className="pricing-compare-tier-text pricing-compare-tier-text--essentials">{row.essentials}</span>
                      }
                    </span>
                    <span className="pricing-compare-tier">
                      {row.pro === true
                        ? <CheckIcon color={PRO_GOLD} />
                        : row.pro === false
                          ? <XIcon />
                          : <span className="pricing-compare-tier-text pricing-compare-tier-text--pro">{row.pro}</span>
                      }
                    </span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <PricingBrokerSupportTable connectTradeLaunched={connectTradeLaunched} />

      <section className="pricing-section">
        <div className="pricing-section-inner">
          <h2 className="pricing-section-title">Getting started</h2>
          <div className="pricing-getting-started">
            <ol className="pricing-steps">
              <li>
                <span className="pricing-steps__index">1</span>
                <div>
                  <h3>Create an account</h3>
                  <p>The 14-day trial starts without a credit card.</p>
                </div>
              </li>
              <li>
                <span className="pricing-steps__index">2</span>
                <div>
                  <h3>Connect a broker</h3>
                  <p>
                    {connectTradeLaunched
                      ? `Link an account you already have. ${brokerCountLabel} are supported.`
                      : 'Link a TradeStation account you already have.'}
                  </p>
                </div>
              </li>
              <li>
                <span className="pricing-steps__index">3</span>
                <div>
                  <h3>Set your rules</h3>
                  <p>
                    <Link to="/learn" className="pricing-inline-link">Onboarding</Link>
                    {' '}walks through risk limits and the trading screen.
                  </p>
                </div>
              </li>
            </ol>
            <div className="pricing-getting-started__panel">
              {connectTradeLaunched ? (
                <>
                  <h3>Connect your broker</h3>
                  <p>
                    Account data comes from the broker you link. Order entry is part of Trade, on {tradeBrokers}.
                  </p>
                  <button
                    type="button"
                    className="landing-cta-btn pricing-panel-cta"
                    onClick={() => handleStartTrial()}
                  >
                    Start Today
                  </button>
                </>
              ) : (
                <>
                  <a href={TRADESTATION_AOP_URL} target="_blank" rel="noopener noreferrer" className="pricing-ts-logo-link">
                    <img
                      src="/images/tradestation-logo.png"
                      alt="TradeStation"
                      className="pricing-ts-logo"
                    />
                  </a>
                  <h3>TradeStation account required</h3>
                  <p>
                    Account data and Trade orders connect through TradeStation. You need an active TradeStation account.
                  </p>
                  <a
                    href={TRADESTATION_AOP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="landing-cta-btn landing-cta-btn--secondary pricing-panel-cta"
                  >
                    Open a TradeStation account
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="pricing-section-inner">
          <h2 className="pricing-section-title">Questions before you subscribe?</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>Can I cancel anytime?</h4>
              <p>Yes. No cancellation fees or penalties.</p>
            </div>
            <div className="faq-item">
              <h4>How does the 14-day trial work?</h4>
              <p>No credit card. After 14 days, subscribe to keep access. Cancel before then and you are not charged.</p>
            </div>
            <div className="faq-item">
              <h4>What&apos;s the difference between Track and Trade?</h4>
              <p>Track is free. It alerts you when a rule breaks, and covers journaling and analytics while you place orders through your broker. Trade adds charts, order entry, position sizing, and rules that block order placement.</p>
            </div>
            <div className="faq-item">
              <h4>Do I need TradeStation?</h4>
              <p>
                {connectTradeLaunched ? (
                  <>PrecisionTrader connects live to {brokerCountLabel} for account data. Trade — charts, order entry, and rules that block order placement — is on {tradeBrokers}.</>
                ) : (
                  <>
                    Yes, for now. PrecisionTrader connects to TradeStation for account data and (on Trade) orders. More broker integrations are planned — you can{' '}
                    <Link to="/features" className="pricing-inline-link">request another broker</Link>.
                  </>
                )}
              </p>
            </div>
            <div className="faq-item">
              <h4>What about market data fees?</h4>
              <p>
                {connectTradeLaunched
                  ? 'Market data comes through your connected broker. Their fees are separate from PrecisionTrader.'
                  : 'Market data comes through your TradeStation account. Their fees are separate from PrecisionTrader.'}
              </p>
            </div>
            <div className="faq-item">
              <h4>How often do you ship improvements?</h4>
              <p>Every week. Features and fixes ship on that cadence.</p>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter containerClass="pricing-container" showRiskDisclaimer={true} showTradeStationDisclosure={true} />
      </div>
    </div>
  );
};

export default Pricing;
